import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { getDatabase, ref, get, child } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { BaseType, BuildComponentTypeDefinition, ComponentLifetime, MaintenanceTemplate } from '../types/Enums';
import { translate } from '../types/EnumTranslations';
import { setBuildComponentTypes } from "../Helpers/BuildComponentTypesStore";

// The context value type
interface BuildComponentTypesContextType {
    types: Record<string, BuildComponentTypeDefinition>;
    getTranslation: (key: string | undefined, fallbackBaseType?: BaseType | string) => string;
    getBaseType: (key: string) => BaseType | undefined;
    getLifetime: (key: string) => ComponentLifetime | undefined;
    getTemplates: (key: string) => MaintenanceTemplate[];
    getAllTemplates: () => MaintenanceTemplate[];
    getTemplateById: (id: string) => MaintenanceTemplate | null;
    getTypesForBaseType: (baseType: BaseType) => string[];
    refresh: () => Promise<void>;
}

const BuildComponentTypesContext = createContext<BuildComponentTypesContextType | undefined>(undefined);

export const useBuildComponentTypes = () => {
    const context = useContext(BuildComponentTypesContext);
    if (!context) {
        throw new Error('useBuildComponentTypes must be used within a BuildComponentTypesProvider');
    }
    return context;
};

export const BuildComponentTypesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [types, setTypes] = useState<Record<string, BuildComponentTypeDefinition>>({});
    const [flatTemplates, setFlatTemplates] = useState<MaintenanceTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null); // null = checking, true/false = determined

    // Fetch types function - extracted so it can be called manually
    const fetchTypes = useCallback(async () => {
        try {
            const db = getDatabase();
            const dbRef = ref(db);

            // Fetch types and flat templates together and set them together, so
            // consumers never see new types with the previous templates
            const [typesSnapshot, templatesSnapshot] = await Promise.all([
                get(child(dbRef, 'config/buildComponentTypes')),
                get(child(dbRef, 'config/maintenanceTemplates'))
            ]);

            const data = typesSnapshot.exists() ? typesSnapshot.val() : {};
            const templatesArray: MaintenanceTemplate[] = templatesSnapshot.exists()
                ? Object.entries(templatesSnapshot.val()).map(
                    ([id, template]) => ({
                        ...(template as Omit<MaintenanceTemplate, 'id'>),
                        id
                    })
                )
                : [];

            setTypes(data);
            setBuildComponentTypes(data);
            setFlatTemplates(templatesArray);

            setError(null);
        } catch (err) {
            console.error("Error fetching build component types:", err);
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Check authentication state first
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsAuthenticated(!!user);
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        // Wait for auth state to be determined
        if (isAuthenticated === null) {
            return;
        }

        // Only fetch types if user is authenticated
        if (!isAuthenticated) {
            setLoading(false);
            setError(null); // Clear any previous errors when signing out
            return;
        }

        // User is authenticated - fetch types
        setLoading(true);
        setError(null);
        fetchTypes();
    }, [isAuthenticated, fetchTypes]);

    const getTranslation = useCallback((key: string | undefined, fallbackBaseType?: BaseType | string): string => {
        // When the specific type can't be resolved, fall back to the component's
        // baseType translation (if provided) rather than leaking the raw key or
        // showing "Odefinierat".
        const fallback = () => (fallbackBaseType ? translate(fallbackBaseType) : "Odefinierat");
        if (!key) return fallback();
        if (!types[key]) return fallback();
        // Default to Swedish 'sv'
        return types[key].translations?.sv || fallback();
    }, [types]);

    const getBaseType = useCallback((key: string): BaseType | undefined => {
        return types[key]?.baseType;
    }, [types]);

    const getLifetime = useCallback((key: string): ComponentLifetime | undefined => {
        return types[key]?.maintenance?.lifetime;
    }, [types]);

    const getTemplates = useCallback((key: string): MaintenanceTemplate[] => {
        // Return templates from flat structure filtered by primaryComponentType
        return flatTemplates.filter(t => t.primaryComponentType === key);
    }, [flatTemplates]);

    const getAllTemplates = useCallback((): MaintenanceTemplate[] => {
        return flatTemplates;
    }, [flatTemplates]);

    const getTemplateById = useCallback((id: string): MaintenanceTemplate | null => {
        return flatTemplates.find(t => t.id === id) || null;
    }, [flatTemplates]);

    const getTypesForBaseType = useCallback((baseType: BaseType): string[] => {
        return Object.entries(types)
            .filter(([_, definition]) => definition.baseType === baseType)
            .map(([key, _]) => key);
    }, [types]);

    // Refresh function to manually reload configuration
    const refresh = useCallback(async () => {
        await fetchTypes();
    }, [fetchTypes]);

    const value = useMemo(() => ({
        types,
        getTranslation,
        getBaseType,
        getLifetime,
        getTemplates,
        getAllTemplates,
        getTemplateById,
        getTypesForBaseType,
        refresh
    }), [types, getTranslation, getBaseType, getLifetime, getTemplates, getAllTemplates, getTemplateById, getTypesForBaseType, refresh]);

    // Wait for auth state to be determined before deciding what to render
    if (isAuthenticated === null) {
        // Still checking authentication - show minimal loading or nothing
        return (
            <BuildComponentTypesContext.Provider value={value}>
                {children}
            </BuildComponentTypesContext.Provider>
        );
    }

    // If not authenticated, allow rendering (login page will show)
    // Only block rendering if authenticated but still loading or has error
    if (isAuthenticated && loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column',
                gap: '20px'
            }}>
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Laddar...</span>
                </div>
                <p className="text-muted">Laddar komponenttyper...</p>
            </div>
        );
    }

    if (isAuthenticated && error) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column',
                gap: '20px',
                padding: '20px'
            }}>
                <div className="alert alert-danger" role="alert" style={{ maxWidth: '500px' }}>
                    <h4 className="alert-heading">Fel vid laddning av komponenttyper</h4>
                    <p>{error.message}</p>
                    <hr />
                    <div className="d-flex gap-2">
                        <button 
                            className="btn btn-danger"
                            onClick={() => {
                                setError(null);
                                setLoading(true);
                                fetchTypes();
                            }}
                        >
                            Försök igen
                        </button>
                        <button 
                            className="btn btn-outline-secondary"
                            onClick={() => window.location.reload()}
                        >
                            Ladda om sidan
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <BuildComponentTypesContext.Provider value={value}>
            {children}
        </BuildComponentTypesContext.Provider>
    );
};
